/*    
<summary>
  This functional component "AddStaffManagement" defines the props like initial values and functions for the child form component.
  Developer:Sahil Bharti, Created Date:01-Jan-2025
</summary>
<returns>Returns JSX</returns>
*/

import { cloneDeep } from "lodash";
import { observer } from "mobx-react";
import React, { useEffect } from "react";
import toast from "react-hot-toast";
import { registrationTypeOptions } from "../../../../constants/options.constant";
import { useStore } from "../../../../contexts/store-provider";
import { IAddProductManagement } from "../../../../models/forms/IAddProduct";
import { formatMessage } from "../../../../translations/format-message";
import AddProductManagementForm from "../forms/add-edit-product-form";
import { ActionType } from "../../../../constants/enums/action-type-enum";
import { ModuleType } from "../../../../constants/enums/module-type-enum";
import { isOperationPermittedRoleBase } from "../../../../helpers/action-permission-helper";
import userTypeEnum from "../../../../constants/enums/user-type-enum";
import { getUserTypeFromLocalStorage } from "../../../../helpers/localstorage-helper";

interface IProps {
  id: number;
  modalClosed: () => void;
  viewOnly?: boolean;
}

const AddEditProductManagement = (props: IProps) => {
  const {
    productManagementStore,
    masterManagementStore,
    categoryManagementStore,
  } = useStore();
  const {
    GetProductManagementByIdService,
    resetAddUpdateProductManagementState,
    resetProductManagementDetail,
    productState,
    productDetail,
    addUpdateProductManagementState,
    AddProductManagementService,
    UpdateProductManagementService,
    deleteProductManagementState,
    DeleteProductManagementService,
    resetDeleteProductManagement,
  } = productManagementStore;
  const {
    resetCategoryList,
    allCategoryManagement,
    GetCategoryManagementListFilterService,
    inProgress,
  } = categoryManagementStore;
  const {
    getAllTaxOptions,
    GetAllTaxOptionService,
    getAllBusinessTypes,
    GetAllBusinessTypeService,
  } = masterManagementStore;
  const initialValues: IAddProductManagement = productDetail;
  const userType = getUserTypeFromLocalStorage();
  /**
   * The useEffect Hook lets you perform side effects in function component.
   * The function we pass to the useEffect Hook runs when the component is mounted and when it is re-rendered
   * while a dependency of the useEffect has changed.
   */
  useEffect(() => {
    GetAllBusinessTypeService();
    let userType = getUserTypeFromLocalStorage();
    if (userTypeEnum[userTypeEnum.SuperAdmin] !== userType) {
      GetAllTaxOptionService();
    }
    if (props.id > 0) {
      GetProductManagementByIdService(props.id);
      // GetCategoryManagementListFilterService(initialValues?.BusinessTypeId, 2);
    }
  }, [props.id]);

  useEffect(() => {
    let userType = getUserTypeFromLocalStorage();
    if (
      userTypeEnum[userTypeEnum.SuperAdmin] === userType &&
      initialValues &&
      Number(initialValues.BusinessTypeId) > 0
    ) {
      GetAllTaxOptionService(initialValues.BusinessTypeId);
    }
  }, [GetAllTaxOptionService, initialValues.BusinessTypeId]);


  // useEffect(() => {
  //   if (props.id > 0) {
  //     GetCategoryManagementListFilterService(initialValues?.BusinessTypeId, 2);
  //   }
  // }, [initialValues]);

  /**
   * The function we pass to the useEffect Hook runs when the component is mounted and when it is re-rendered while
   * dependency "addUpdateStaffManagementSuccess" of the useEffect changed.
   */
  useEffect(() => {
    if (addUpdateProductManagementState.success) {
      if (props.id === -1) toast.success(formatMessage("added_success"));
      else toast.success(formatMessage("updated_success"));
      onModalClosed();
    }
  }, [addUpdateProductManagementState.success]);

  /**
   * The function we pass to the useEffect Hook runs when the component is mounted and when it is re-rendered while
   * dependency "staffDetailError" of the useEffect changed.
   */
  useEffect(() => {
    if (productState.error) {
      toast.error(formatMessage(productState.error));
    }
  }, [productState.error]);

  /**
   * The function we pass to the useEffect Hook runs when the component is mounted and when it is re-rendered while
   * dependency "addUpdateStaffManagementError" of the useEffect changed.
   */
  useEffect(() => {
    if (addUpdateProductManagementState.error) {
      toast.error(formatMessage(addUpdateProductManagementState.error));
      resetAddUpdateProductManagementState();
    }
  }, [addUpdateProductManagementState.error]);

  /**
   * The function we pass to the useEffect Hook runs when the component is mounted and when it is re-rendered while
   * dependency "addUpdateStoreManagementSuccess" of the useEffect changed.
   */
  useEffect(() => {
    if (deleteProductManagementState.success) {
      toast.success(formatMessage("deleted_success"));
      onDeleteModalClosed();
    }
  }, [deleteProductManagementState.success]);

      useEffect(() => {
        if (deleteProductManagementState.error) {
          toast.error(formatMessage(deleteProductManagementState.error));
          resetDeleteProductManagement();
          props.modalClosed();
        }
      }, [deleteProductManagementState.error, resetDeleteProductManagement]);

  /**
   * This function adds the staff when "Id < 0" and updates the Existing staff when Id > 0 by providing form values to the
   * correponding function
   */
  const onSubmitForm = (values: IAddProductManagement) => {
    let tempValues = cloneDeep(values);
    tempValues.ProductName = values.ProductName.trim();
    if (props.id < 0) AddProductManagementService(tempValues);
    else
      UpdateProductManagementService(props.id, {
        ...tempValues,
        IsBarcodeChanged: initialValues?.BarCode !== tempValues.BarCode,
        StoreId: initialValues.StoreId ? initialValues.StoreId : null
      });
  };

  /**
   * This function closes the pop up modal and reset getstaffdetails, addUpdate staff observables (success, inprogress
   * and error).
   */
  const onModalClosed = () => {
    resetProductManagementDetail();
    if (userType === userTypeEnum[userTypeEnum.SuperAdmin]) {
      resetCategoryList();
    }
    props.modalClosed();
  };

  const handleDeleteSubmitHandler = () => {
    DeleteProductManagementService(props.id);
  };

  /**
   * This function closes the pop up modal and reset getstoredetails, addUpdate store observables (success, inprogress
   * and error).
   */
  const onDeleteModalClosed = () => {
    resetProductManagementDetail();
    resetCategoryList();
    props.modalClosed();
  };
  return (
    <React.Fragment>
      <AddProductManagementForm
        id={props.id}
        initialValues={initialValues}
        getProductManagementLoading={
          productState.inProgress ||
          inProgress ||
          addUpdateProductManagementState.inProgress
        }
        submitHandler={onSubmitForm}
        onModalClosed={onModalClosed}
        businessTypeOptions={getAllBusinessTypes}
        categoryOptions={allCategoryManagement}
        taxOptions={getAllTaxOptions}
        registrationTypeOptions={registrationTypeOptions}
        handleDeleteSubmitHandler={handleDeleteSubmitHandler}
        deleteActionLoading={deleteProductManagementState.inProgress}
        viewOnly={
          props.viewOnly ? props.viewOnly : (props.id > 0
            ? !isOperationPermittedRoleBase(
                ModuleType.ProductManagement,
                ActionType.Add
              )
            : false)
        }
      />
    </React.Fragment>
  );
};

export default observer(AddEditProductManagement);
